import { login as loginApi, logout as logoutApi, getInfo } from '@/apis/auth/auth'
import { getUserList, updateProfile, updatePassword } from '@/apis/user/user'
import { setToken, setUser, getUser, removeToken, removeUser } from '@/utils/auth'

const silentError = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.debug('[User]', ...args.map(a => a?.msg || a?.message || a))
  }
}

export default {
  namespaced: true,
  state: {
    currentUser: {},
    isAuthenticated: false,
    users: {
      list: [],
      total: 0
    }
  },
  mutations: {
    SET_CURRENT_USER(state, user) {
      state.currentUser = user
    },
    SET_AUTHENTICATED(state, isAuthenticated) {
      state.isAuthenticated = isAuthenticated
    },
    SET_USERS(state, { list, total }) {
      state.users.list = list
      state.users.total = total
    },
    UPDATE_CURRENT_USER(state, user) {
      state.currentUser = { ...state.currentUser, ...user }
    }
  },
  actions: {
    login({ commit, dispatch }, userInfo) {
      // 登录前先清除旧数据
      dispatch('clearLoginInfo')
      
      return new Promise((resolve, reject) => {
        loginApi(userInfo).then(data => {
          // 保存 token (request.js 成功时返回的是 apiResponse.data)
          setToken(data.token)
          // 获取并保存用户信息
          return getInfo()
        }).then(user => {
          if (user) {
            const permissionSource = Array.isArray(user.permissions) ? user.permissions : []
            const normalizedUser = {
              ...user,
              permissions: Array.from(new Set(permissionSource.filter(Boolean)))
            }
            // 确保后端返回了权限和角色信息
            console.log('User info fetched after login:', normalizedUser)
            commit('SET_CURRENT_USER', normalizedUser)
            commit('SET_AUTHENTICATED', true)
            setUser(normalizedUser)
            resolve(normalizedUser)
          } else {
            reject(new Error('获取用户信息失败'))
          }
        }).catch(error => {
          silentError('Login action error:', error)
          reject(error)
        })
      })
    },
    logout({ dispatch }) {
      return new Promise((resolve) => {
        logoutApi().then(() => {
          dispatch('clearLoginInfo')
          resolve()
        }).catch(() => {
          dispatch('clearLoginInfo')
          resolve()
        })
      })
    },
    refreshCurrentUser({ commit }) {
      return new Promise((resolve, reject) => {
        getInfo().then(user => {
          if (user) {
            const permissionSource = Array.isArray(user.permissions) ? user.permissions : []
            const normalizedUser = {
              ...user,
              permissions: Array.from(new Set(permissionSource.filter(Boolean)))
            }
            commit('SET_CURRENT_USER', normalizedUser)
            commit('SET_AUTHENTICATED', true)
            setUser(normalizedUser)
            resolve(normalizedUser)
          } else {
            reject(new Error('获取用户信息失败'))
          }
        }).catch(error => {
          reject(error)
        })
      })
    },
    // 获取用户列表
    async fetchUsers({ commit }, params) {
      try {
        const data = await getUserList(params)
        commit('SET_USERS', { list: data.list, total: data.total })
        return data
      } catch (error) {
        silentError('获取用户列表失败:', error)
        throw error
      }
    },
    // 更新个人资料
    async updateProfile({ commit }, profileData) {
      try {
        await updateProfile(profileData)
        commit('UPDATE_CURRENT_USER', profileData)
        // 更新本地存储的用户信息
        const user = getUser() || {}
        const updatedUser = { ...user, ...profileData }
        setUser(updatedUser)
        return updatedUser
      } catch (error) {
        silentError('更新个人资料失败:', error)
        throw error
      }
    },
    // 更新密码
    async updatePassword({ commit }, passwordData) {
      try {
        await updatePassword(passwordData)
        return true
      } catch (error) {
        silentError('更新密码失败:', error)
        throw error
      }
    },
    // 清除本地登录信息
    clearLoginInfo({ commit }) {
      // 1. 清除 Vuex 状态
      commit('SET_CURRENT_USER', {})
      commit('SET_AUTHENTICATED', false)
      
      // 2. 清除 Cookie 中的 Token 和用户信息
      removeToken()
      removeUser()
      
      // 3. 兜底清除 localStorage/sessionStorage
      localStorage.removeItem('token')
      localStorage.removeItem('tokenExpire')
      sessionStorage.removeItem('token')
      sessionStorage.removeItem('tokenExpire')
      
      console.log('Login info cleared')
    }
  },
  getters: {
    currentUser: state => state.currentUser,
    isAuthenticated: state => state.isAuthenticated,
    hasRole: (state) => (roleCode) => {
      if (!state.currentUser) return false
      if (state.currentUser.superAdmin === 1 && roleCode === 'superAdmin') return true
      return state.currentUser.roleCode === roleCode
    }
  }
}
